<script>
    import router from "page";
    import { onMount } from "svelte";
    import { isLoading, locale } from "../stores.js";

    import Modal from "../components/Modal.svelte";
    import DeleteModal from "../components/DeleteModal.svelte";

    import { TabulatorFull as Tabulator } from "tabulator-tables";

    import { getLocaleResource } from "../utils/locales.js";

    import {
        fetchGet,
        fetchPost,
        fetchPatch,
        fetchDelete,
    } from "../utils/utilsFetch.js";

    import {
        productPictureLinkFormatter,
        numberFormatter,
        oneDecimalFormatter,
        percentFormatter,
        keywordFilter,
    } from "../utils/utilsTabulator.js";

    let table, data, tableElement;

    onMount(async () => {
        $isLoading = true;

        data = await fetchGet(`/api/users`);

        table = new Tabulator(tableElement, {
            height: "100%",
            data: data,
            layout: "fitDataFill",
            vertAlign: "middle",
            columnCalcs: "both",
            columns: [
                {
                    title: getLocaleResource($locale, "sku"),
                    field: "companyKey",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
                {
                    title: getLocaleResource($locale, "client"),
                    field: "companyName",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
                {
                    title: getLocaleResource($locale, "email"),
                    field: "email",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
                {
                    title: getLocaleResource($locale, "cart"),
                    columns: [
                        {
                            title: getLocaleResource($locale, "products"),
                            field: "cartSize",
                        },
                        {
                            title: getLocaleResource($locale, "amount"),
                            field: "cartValue",
                        },
                    ],
                },
                {
                    title: getLocaleResource($locale, "revenue"),
                    field: "totalOrderValue",
                },
                {
                    title: getLocaleResource($locale, "last"),
                    columns: [
                        {
                            title: getLocaleResource($locale, "login"),
                            field: "lastLoginAt",
                        },
                        {
                            title: getLocaleResource($locale, "order"),
                            field: "lastOrderAt",
                        },
                    ],
                },
            ],
        });

        table.on("rowClick", (e, row) => {
            let user = row._row.data;
            getUser(user.id);
        });

        $isLoading = false;
    });

    let showUserModal, user, changeUserPasswordInput;

    async function getUser(userId) {
        $isLoading = true;
        user = await fetchGet(`/api/users/${userId}`);
        showUserModal = true;
        $isLoading = false;
    }

    async function updateUser(userId) {
        await fetchPatch(`/api/users/${userId}`, user);
        refreshUsers();
    }

    async function changePassword(userId) {
        await fetchPatch(`/api/users/${userId}/password`, {
            password: changeUserPasswordInput,
        });
        changeUserPasswordInput = "";
        showUserModal = false;
    }

    let showDeleteModal;
    async function deleteUser(userId) {
        await fetchDelete(`/api/users/${userId}`);
        showUserModal = false;
        refreshUsers();
    }

    let showAddUserModal, newUserCompanyKey, newUserEmail, newUserPassword;

    async function addNewUser() {
        $isLoading = true;

        await fetchPost(`/api/users`, {
            email: newUserEmail,
            companyKey: newUserCompanyKey,
            password: newUserPassword,
        });

        newUserCompanyKey = "";
        newUserEmail = "";
        newUserPassword = "";

        refreshUsers();

        showAddUserModal = false;
        $isLoading = false;
    }

    async function refreshUsers() {
        table.replaceData(await fetchGet(`/api/users`));
    }
</script>

<Modal bind:showModal={showUserModal}>
    <div slot="title">{getLocaleResource($locale, "user")}</div>
    <div slot="body">
        <div class="user">
            <div>
                {user.companyKey} - {user.companyName}
            </div>

            <div>
                <input
                    type="text"
                    placeholder={getLocaleResource($locale, "email")}
                    bind:value={user.email}
                    on:blur={() => {
                        updateUser(user.id);
                    }}
                />
            </div>

            <div style="display: flex; align-items:center;">
                <input
                    type="password"
                    placeholder={getLocaleResource($locale, "password")}
                    bind:value={changeUserPasswordInput}
                />
                <button
                    class="btn btn-blue"
                    on:click={() => {
                        changePassword(user.id);
                    }}
                    style="margin-left: 10px;"
                >
                    {getLocaleResource($locale, "change")}
                </button>
            </div>

            <button
                class="btn btn-red"
                on:click={() => {
                    showDeleteModal = true;
                }}
            >
                {getLocaleResource($locale, "delete")}
            </button>
        </div>
    </div>
</Modal>

<DeleteModal
    bind:showDeleteModal
    deleteMessage={getLocaleResource($locale, "areYouSure")}
    deleteCallback={() => {
        deleteUser(user.id);
    }}
/>

<Modal bind:showModal={showAddUserModal}>
    <div slot="title">
        {getLocaleResource($locale, "add")}
        {getLocaleResource($locale, "user")}
    </div>
    <div slot="body">
        <div class="form">
            <input
                placeholder={`${getLocaleResource(
                    $locale,
                    "companyKey"
                )} ${getLocaleResource($locale, "client")}`}
                bind:value={newUserCompanyKey}
            />
            <input
                placeholder={getLocaleResource($locale, "email")}
                bind:value={newUserEmail}
            />
            <input
                type="password"
                placeholder={getLocaleResource($locale, "password")}
                bind:value={newUserPassword}
            />

            <button class="btn btn-blue" on:click={addNewUser}>
                {getLocaleResource($locale, "add")}
            </button>
        </div>
    </div>
</Modal>

<div class="container">
    <div class="header">
        <button
            class="btn btn-blue"
            on:click={() => {
                showAddUserModal = true;
            }}
        >
            {getLocaleResource($locale, "add")}
            {getLocaleResource($locale, "user")}
        </button>
    </div>

    <div class="content">
        <div class="table" bind:this={tableElement} />
    </div>
</div>

<style>
    .container {
        display: grid;
        grid-template-columns: 1fr;
        /* calc sprecuva tabelata da ide nadvor od content*/
        /* grid-template-rows: 30px 45px calc(100vh - 95px);  */
        grid-template-rows: auto 1fr;
        height: 100%;
    }

    .header {
        grid-row: 1/2;

        display: flex;
        flex-wrap: wrap;
        align-items: center;
        /* background: #ccc; */
        padding: 10px;
    }

    .header div {
        margin-right: 20px;
    }

    .content {
        padding: 0px 10px 10px 10px;
        grid-row: 2/-1;

        overflow: auto;
    }

    .user div {
        margin-bottom: 10px;
    }

    .form {
        display: flex;
        flex-direction: column;
    }

    .form input {
        margin-bottom: 10px;
    }
</style>
